import { useState } from 'react';
import type { Bidder } from '../data/mockLots';
import { revealProps } from '../lib/motion';
import {
  ACTIVATION_BONUS_BIDS,
  type Referral,
  type ReferralStage,
  type ReferralSummary,
} from '../lib/referral';
import { buildReferralDeeplink, shareReferral } from '../lib/tg';
import { BidderAvatar } from './BidderPill';
import { SectionHeader } from './SectionHeader';

const ROWS_PER_PAGE = 5;

const STAGE_STYLE: Record<ReferralStage, { chip: string; label: string }> = {
  invited: { chip: 'text-fg-muted border-edge-bright', label: 'Pending' },
  activated: { chip: 'text-primary border-primary/40', label: 'Active' },
};

// Stable avatar hue for a stubbed referral (no numeric user id yet). Mirrors
// placeholderBidder's *53 spread so it lands in the same palette; swap for
// placeholderBidder(user_id, …) once referrals carry real ids.
function hueFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h * 53) % 360;
}

// Compact row in the bid-history idiom: h-9, xs avatar, name, bonus bids earned
// in the middle, stage chip on the right. Tappable through to the friend's TG
// (like a bid-history row) so the inviter can nudge a still-invited referral.
function ReferralRow({ referral: r, index }: { referral: Referral; index: number }) {
  const handle = r.username ?? r.name.replace(/^@/, '');
  const bidder: Bidder = {
    id: r.id,
    firstName: r.name,
    username: r.username,
    initial: handle.charAt(0).toUpperCase() || '?',
    hue: hueFromId(r.id),
    bidCount: 0,
  };
  const style = STAGE_STYLE[r.stage];
  const activated = r.stage === 'activated';

  const inner = (
    <>
      <BidderAvatar bidder={bidder} size="xs" />
      <span className="font-medium text-[12px] text-fg truncate flex-1 min-w-0">{r.name}</span>
      {activated && (
        <span className="font-mono text-[11px] tabular-nums shrink-0 text-primary">
          +{r.bidsEarned}
          <span className="text-fg-dim ml-1">bids</span>
        </span>
      )}
      <span
        className={`shrink-0 font-mono text-[8.5px] tracking-[0.1em] uppercase border px-1.5 py-0.5 ${style.chip}`}
      >
        {style.label}
      </span>
    </>
  );

  const reveal = revealProps(index, {
    base: 60,
    step: 45,
    className: 'flex items-center gap-2.5 h-9 px-2.5',
  });
  if (r.username) {
    return (
      <a
        href={`https://t.me/${r.username}`}
        target="_blank"
        rel="noreferrer"
        className={`${reveal.className} hover:bg-bg-elev/60 transition-colors`}
        style={reveal.style}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className={reveal.className} style={reveal.style}>
      {inner}
    </div>
  );
}

// Page-by-page referral list with arrow controls, mirroring a closed lot's
// FullBidHistory. Kept off the vertical scroll axis so it doesn't fight the lot
// history below; the row slot is fixed-height on full pages to avoid reflow.
function ReferralRail({ referrals }: { referrals: Referral[] }) {
  const [pageIdx, setPageIdx] = useState(0);
  const pageCount = Math.ceil(referrals.length / ROWS_PER_PAGE);
  const start = pageIdx * ROWS_PER_PAGE;
  const rows = referrals.slice(start, start + ROWS_PER_PAGE);
  const canPrev = pageIdx > 0;
  const canNext = pageIdx < pageCount - 1;
  // h-9 rows (36px) × ROWS_PER_PAGE — reserved on full pages so paging never
  // reflows the lot history below.
  const isFullSlot = rows.length === ROWS_PER_PAGE;

  return (
    <section>
      <div className={`px-3 flex flex-col ${isFullSlot ? 'min-h-[180px]' : ''}`}>
        {rows.map((r, i) => (
          <ReferralRow key={r.id} referral={r} index={i} />
        ))}
      </div>

      {pageCount > 1 && (
        <div className="px-3 mt-2 pt-3 border-t border-edge flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => canPrev && setPageIdx(pageIdx - 1)}
            disabled={!canPrev}
            className="h-10 w-20 font-mono text-[11px] tracking-[0.2em] text-primary disabled:text-fg-dim disabled:cursor-default hover:text-fg transition-colors flex items-center justify-center gap-1.5"
            aria-label="previous referrals"
          >
            <span>←</span>
            <span>prev</span>
          </button>
          <span className="font-mono text-[10px] tracking-[0.18em] text-fg-muted tabular-nums">
            {start + 1}–{start + rows.length} of {referrals.length}
          </span>
          <button
            type="button"
            onClick={() => canNext && setPageIdx(pageIdx + 1)}
            disabled={!canNext}
            className="h-10 w-20 font-mono text-[11px] tracking-[0.2em] text-primary disabled:text-fg-dim disabled:cursor-default hover:text-fg transition-colors flex items-center justify-center gap-1.5"
            aria-label="more referrals"
          >
            <span>next</span>
            <span>→</span>
          </button>
        </div>
      )}
    </section>
  );
}

export function ReferralPanel({ summary }: { summary: ReferralSummary }) {
  const [copied, setCopied] = useState(false);
  const link = buildReferralDeeplink(summary.code);
  const linkDisplay = link.replace(/^https?:\/\//, '');
  const friendCount = summary.referrals.length;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('referral.copy_failed', err);
    }
  };

  const share = () =>
    shareReferral(
      summary.code,
      `Win CS2 skins for cents on SkinStep. Join with my code ${summary.code}.`,
    );

  return (
    <>
      <SectionHeader label="Refer & earn" />

      <section
        {...revealProps(1, {
          className: 'relative mx-3 border border-edge bg-bg-elev/40 overflow-hidden',
        })}
      >
        {/* Invite code + actions */}
        <div className="px-4 pt-4 pb-4">
          <span className="font-mono text-[9px] tracking-[0.3em] text-fg-muted">
            YOUR INVITE CODE
          </span>
          <div className="relative hud-corners mt-2.5 border border-edge-bright bg-bg-deep/60 px-3 py-2.5">
            <p
              className="font-mono font-bold text-[18px] tracking-[0.15em] text-primary truncate"
              style={{ textShadow: '0 0 12px rgba(0,229,255,0.35)' }}
            >
              {summary.code}
            </p>
            <p className="font-mono text-[9px] text-fg-dim truncate mt-1">{linkDisplay}</p>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={copy}
              className="h-11 px-4 inline-flex items-center justify-center border border-edge-bright text-fg-muted font-mono text-[11px] tracking-[0.22em] uppercase hover:text-fg hover:border-fg-muted active:scale-[0.99] transition-all"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={share}
              className="flex-1 h-11 inline-flex items-center justify-center gap-2 border border-accent/50 bg-accent/10 text-accent font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-accent/20 hover:border-accent/70 active:scale-[0.99] transition-all"
            >
              <span aria-hidden>↗</span> Share invite
            </button>
          </div>
          <p className="mt-3 text-[11px] leading-snug text-fg-muted">
            Earn <span className="text-fg">{ACTIVATION_BONUS_BIDS} bonus bids</span> when a friend
            activates — and more as they bid.
          </p>
        </div>
      </section>

      {friendCount > 0 ? (
        <>
          <SectionHeader label="Your referrals" count={friendCount} />
          <ReferralRail referrals={summary.referrals} />
        </>
      ) : (
        <div className="mx-3 mt-3 border border-dashed border-edge bg-bg-elev/20 px-4 py-6 text-center">
          <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">NO REFERRALS YET</p>
          <p className="text-[12px] text-fg-muted mt-2 leading-snug">
            Share your code — every friend who joins earns you bonus bids.
          </p>
        </div>
      )}

      <p className="mx-4 mt-3 font-mono text-[9px] leading-relaxed text-fg-dim">
        Self-referral and referral spam are banned — accruals stay predictable.
      </p>
    </>
  );
}
